import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, BarChart, Headset, Shield, FileText, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Seo from '../components/Seo';
import { images } from '../mock';
import { iconMap } from '../lib/icons';

const Partners = () => {
  const targetPartners = [
    {
      icon: "Hospital",
      title: "Hospitals",
      description: "Discharge transportation and outpatient appointment services"
    },
    {
      icon: "Building2",
      title: "Nursing Homes",
      description: "Regular transportation for facility residents to medical appointments"
    },
    {
      icon: "Activity",
      title: "Dialysis Centers",
      description: "Recurring transportation programs for dialysis patients"
    },
    {
      icon: "Briefcase",
      title: "Managed Care Organizations",
      description: "Network transportation services for plan members"
    },
    {
      icon: "ClipboardList",
      title: "Case Managers",
      description: "Coordinated transportation solutions for client care plans"
    },
    {
      icon: "Stethoscope",
      title: "Medical Practices",
      description: "Patient transportation for specialty appointments"
    }
  ];

  const partnershipBenefits = [
    {
      icon: FileText,
      title: "Contract Transportation Model",
      description: "Flexible contract arrangements with predictable pricing and dedicated service levels"
    },
    {
      icon: BarChart,
      title: "Performance Metrics Tracking",
      description: "Regular reporting on on-time performance, service quality, and trip completion rates"
    },
    {
      icon: Headset,
      title: "Dedicated Account Management",
      description: "Assigned account manager serving as your primary contact for all transportation needs"
    },
    {
      icon: Users,
      title: "Real-Time Dispatch Coordination",
      description: "Responsive dispatch coordination with clear communication and trip updates during operating hours"
    },
    {
      icon: Shield,
      title: "Insurance Coverage",
      description: "Comprehensive insurance with certificates available and additional insured options"
    },
    {
      icon: CheckCircle2,
      title: "Vendor Documentation",
      description: "Vendor procurement packet with licensing and insurance documentation available on request"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <Seo
        title="Healthcare Facility Transportation Partnerships Houston | MoveraCare"
        description="Contract transportation partnerships for hospitals, nursing facilities, dialysis centers, MCOs, and healthcare organizations across Greater Houston. Non-emergency medical transportation coordination for facility teams."
        path="/partners"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Facility Partners', url: '/partners' }]}
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-black">
        <div className="absolute inset-0 opacity-30">
          <img src={images.hospital} alt="Healthcare facility partnership — MoveraCare Mobility" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full mb-6">
              <span className="text-yellow-400 text-sm font-medium">For Healthcare Facilities</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Partner With MoveraCare Mobility
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Reliable transportation infrastructure for healthcare facilities across Greater Houston. Let us handle the logistics while you focus on patient care.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-yellow-400 text-black hover:bg-yellow-500 font-semibold text-lg px-8 py-6">
                Become a Partner
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Target Partners */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Who We Serve</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted transportation partner for diverse healthcare organizations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {targetPartners.map((partner, index) => {
              const IconComponent = iconMap[partner.icon];
              return (
                <Card key={index} className="border-2 hover:border-yellow-400 transition-all">
                  <CardHeader>
                    <div className="w-14 h-14 bg-yellow-400/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-yellow-400" />
                    </div>
                    <CardTitle className="text-xl">{partner.title}</CardTitle>
                    <CardDescription>{partner.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Partnership Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              What you can expect when partnering with MoveraCare Mobility
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Standards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Performance Standards We Maintain
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our commitment to excellence is measured through rigorous performance standards that ensure reliable service for your facility.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-2">Punctuality-Focused Operations</h3>
                    <p className="text-gray-600">We prioritize punctuality because medical appointments can't wait</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-2">Real-Time Communication</h3>
                    <p className="text-gray-600">Immediate updates on pickup times, delays, and trip completion</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-2">Monthly Reporting</h3>
                    <p className="text-gray-600">Detailed performance metrics and service quality reports</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-black mb-2">Compliance Documentation</h3>
                    <p className="text-gray-600">Full documentation available for audits and quality reviews</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src={images.facility} 
                alt="Healthcare facility partnership" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-yellow-400 text-black p-6 rounded-lg shadow-xl">
                <p className="text-2xl font-bold mb-1">Credentialing-Ready</p>
                <p className="text-sm font-semibold">Vendor Documentation Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Partnership Works</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Simple process to establish reliable transportation for your facility
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Initial Contact</h3>
              <p className="text-gray-400">Reach out to discuss your facility's transportation needs</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Needs Assessment</h3>
              <p className="text-gray-400">We evaluate your volume, scheduling, and service requirements</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Contract Agreement</h3>
              <p className="text-gray-400">Flexible contract terms tailored to your facility</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Service Launch</h3>
              <p className="text-gray-400">Dedicated account manager and seamless service begins</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Establish a Transportation Partnership?
          </h2>
          <p className="text-xl text-black/80 mb-8">
            Let's discuss how MoveraCare Mobility can support your facility's transportation needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-black text-yellow-400 hover:bg-gray-900 font-semibold text-lg px-8 py-6">
                Request Partnership Information
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-yellow-400 font-semibold text-lg px-8 py-6">
                <Phone className="mr-2 w-5 h-5" />
                Call 832-925-2128
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
